import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../../../appContext";
import { DashboardContext } from "../../DashboardContext";
import { loadMultiSessionData } from "../../../../api/api";
import { SpeedChart } from "../../../../components/chartjs/SpeedChart";
import { VolumeChart } from "../../../../components/chartjs/VolumeChart";
import { Bubbles } from "../../../../components/bubbles/Bubbles";
import { Diagram } from "../../../../components/diagram/Diagram";
import { MostCommonWord } from "../../../meetingPage/components/mostCommonWord/MostCommonWord";
import { Spinner } from "../../../../components/spinner/Spinner";
import { formatDate3, timestampToDate } from "../../../../utils/dateFormats";
import "./StatisticPage.css";

type TimeRange = 'all' | 'month' | 'week' | 'custom';

interface IProcessedSession {
    id: string;
    date: number | string;
    Volume?: number;
    AvarageSpeed?: number;
    most_common_words?: Array<{word: string, count: number}>;
    MostCommonWords?: Array<{word: string, count: number}>;
    Error?: boolean;
    silence_percentage?: number;
}

export const StatisticPage = () => {
    const { translate, currentLanguage } = useContext(AppContext);
    const { selectedClient, selectedClientId } = useContext(DashboardContext);
    
    console.log('StatisticPage rendered, selectedClientId:', selectedClientId, 'selectedClient:', selectedClient?.name);
    
    const [timeRange, setTimeRange] = useState<TimeRange>('all');
    const [customStartDate, setCustomStartDate] = useState<string>('');
    const [customEndDate, setCustomEndDate] = useState<string>('');
    const [sessions, setSessions] = useState<IProcessedSession[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedWord, setSelectedWord] = useState<string>('');
    useEffect(() => {
        console.log('StatisticPage useEffect triggered, selectedClientId:', selectedClientId);
        if (!selectedClientId) {
            console.log('StatisticPage: No selectedClientId, skipping load');
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        console.log('StatisticPage: Loading data for client:', selectedClientId);
        
        loadMultiSessionData(selectedClientId)
            .then(res => {
                console.log('StatisticPage: API response status:', res.status, res.ok);
                if (!res.ok) throw new Error('Failed to load data');
                return res.json();
            })
            .then((rawData: any[]) => {
                console.log('StatisticPage raw API response:', rawData);
                console.log('StatisticPage raw API response length:', rawData?.length);
                
                const processedSessions: IProcessedSession[] = (rawData || []).flatMap(item => {
                    const sessionId = Object.keys(item)[0];
                    if (!sessionId) return [];
                    
                    const session = item[sessionId];
                    console.log('StatisticPage processing session:', sessionId, 'keys:', Object.keys(session));
                    
                    const result = session.Result || {};
                    
                    const processed = {
                        id: sessionId,
                        date: session.Timestamp || session.date,
                        Volume: session.Volume ?? result.Volume,
                        AvarageSpeed: session.AvarageSpeed ?? result.AvarageSpeed,
                        most_common_words: session.MostCommonWords || session.most_common_words || result.MostCommonWords || result.most_common_words,
                        MostCommonWords: session.MostCommonWords || result.MostCommonWords,
                        silence_percentage: session.silence_percentage ?? result.silence_percentage,
                        Error: session.Error
                    };
                    
                    console.log('StatisticPage session processed:', sessionId, 
                        'Volume:', processed.Volume, 
                        'Speed:', processed.AvarageSpeed,
                        'Words:', processed.most_common_words?.length,
                        'Error:', processed.Error);
                    
                    return [processed];
                });
                
                console.log('StatisticPage processed sessions:', processedSessions.length, 
                    'with speed data:', processedSessions.filter(s => s.AvarageSpeed !== undefined).length,
                    'with volume data:', processedSessions.filter(s => s.Volume !== undefined).length,
                    'with words data:', processedSessions.filter(s => s.most_common_words?.length).length,
                    'without errors:', processedSessions.filter(s => !s.Error).length);
                
                setSessions(processedSessions);
            })
            .catch(err => {
                console.error('StatisticPage Error loading statistics:', err);
                setError(err.message);
                setSessions([]);
            })
            .finally(() => setIsLoading(false));
    }, [selectedClientId]);

    const filteredSessions = useMemo(() => {
        if (!sessions.length) return [];
        
        const now = new Date();
        let startDate: Date | null = null;
        let endDate: Date | null = null;
        
        switch (timeRange) {
            case 'week':
                startDate = new Date(now);
                startDate.setDate(now.getDate() - 7);
                break;
            case 'month':
                startDate = new Date(now);
                startDate.setMonth(now.getMonth() - 1);
                break;
            case 'custom':
                if (customStartDate) startDate = new Date(customStartDate);
                if (customEndDate) endDate = new Date(customEndDate);
                break;
            case 'all':
            default:
                break;
        }
        
        return sessions.filter(session => {
            const sessionDate = typeof session.date === 'string' 
                ? new Date(parseFloat(session.date) * 1000) 
                : new Date(session.date * 1000);
            
            if (startDate && sessionDate < startDate) return false;
            if (endDate && sessionDate > endDate) return false;
            return true;
        });
    }, [sessions, timeRange, customStartDate, customEndDate]);

    const widgetData = useMemo(() => {
        if (!filteredSessions.length) {
            return {
                speedData: { average: 0, speeds: [], sessionNumbers: [] },
                volumeData: { values: [], labels: [] },
                bubblesData: [],
                mostCommonWord: { word: '', count: 0 },
                silenceData: { average: 0 }
            };
        }

        const speedSessions = filteredSessions
            .filter(s => !s.Error && s.AvarageSpeed !== undefined)
            .sort((a, b) => {
                const dateA = typeof a.date === 'string' ? parseFloat(a.date) : a.date;
                const dateB = typeof b.date === 'string' ? parseFloat(b.date) : b.date;
                return dateA - dateB;
            });
        
        const speeds = speedSessions.map(s => s.AvarageSpeed!);
        const average = speeds.length > 0 ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0;

        const volumeSessions = filteredSessions
            .filter(s => !s.Error && s.Volume !== undefined)
            .sort((a, b) => {
                const dateA = typeof a.date === 'string' ? parseFloat(a.date) : a.date;
                const dateB = typeof b.date === 'string' ? parseFloat(b.date) : b.date;
                return dateA - dateB;
            });
        
        const volumeValues = volumeSessions.map(s => s.Volume!);
        const volumeLabels = volumeSessions.map(s => {
            const ts = typeof s.date === 'string' ? parseFloat(s.date) : s.date;
            return formatDate3(timestampToDate(ts));
        });

        const allWords: Record<string, number> = {};
        filteredSessions.forEach(session => {
            const words = session.most_common_words || session.MostCommonWords || [];
            words.forEach(w => {
                allWords[w.word] = (allWords[w.word] || 0) + w.count;
            });
        });
        
        const sortedWords = Object.entries(allWords)
            .map(([word, count]) => ({ word, count }))
            .sort((a, b) => b.count - a.count);
        
        const bubblesData = sortedWords.slice(0, 20).map(w => ({
            label: w.word,
            value: w.count
        }));
        
        const mostCommon = sortedWords[0] || { word: '', count: 0 };

        const silenceSessions = filteredSessions.filter(s => s.silence_percentage !== undefined);
        const avgSilence = silenceSessions.length > 0 
            ? silenceSessions.reduce((a, s) => a + (s.silence_percentage || 0), 0) / silenceSessions.length 
            : 0;

        return {
            speedData: { 
                average, 
                speeds, 
                sessionNumbers: speeds.map((_, i) => i + 1) 
            },
            volumeData: { 
                values: volumeValues, 
                labels: volumeLabels 
            },
            bubblesData,
            mostCommonWord: { word: mostCommon.word, count: mostCommon.count },
            silenceData: { average: avgSilence }
        };
    }, [filteredSessions]);

    const wordsWidgetData = useMemo(() => {
        if (!selectedWord || !filteredSessions.length) return null;

        const sessionCounts: Array<{value: number, label: string}> = [];
        
        const sortedSessions = [...filteredSessions]
            .filter(s => !s.Error)
            .sort((a, b) => {
                const dateA = typeof a.date === 'string' ? parseFloat(a.date) : a.date;
                const dateB = typeof b.date === 'string' ? parseFloat(b.date) : b.date;
                return dateA - dateB;
            });

        sortedSessions.forEach((session, index) => {
            const words = session.most_common_words || session.MostCommonWords || [];
            const wordData = words.find(w => w.word === selectedWord);
            const count = wordData?.count || 0;
            
            sessionCounts.push({
                value: count,
                label: `${translate('statistic_session')} ${index + 1}`
            });
        });

        const totalCount = sessionCounts.reduce((sum, s) => sum + s.value, 0);
        const maxIndex = sessionCounts.reduce((maxIdx, curr, idx, arr) => 
            curr.value > arr[maxIdx].value ? idx : maxIdx, 0);

        return {
            counts: sessionCounts,
            index: maxIndex,
            word: selectedWord,
            totalCount
        };
    }, [selectedWord, filteredSessions, translate]);

    const handleWordBack = () => {
        setSelectedWord('');
    };

    if (!selectedClient) {
        return (
            <div className="statisticPage">
                <div className="statisticPage_empty">
                    {translate('statistic_select_client')}
                </div>
            </div>
        );
    }

    return (
        <div className="statisticPage">
            <div className="statisticPage_header">
                <h2 className="statisticPage_title">{translate('statistic_title')}</h2>
                <div className="statisticPage_filters">
                    <button 
                        className={`statisticPage_filterBtn ${timeRange === 'all' ? 'statisticPage_filterBtn--active' : ''}`}
                        onClick={() => setTimeRange('all')}
                    >
                        {translate('statistic_all_time')}
                    </button>
                    <button 
                        className={`statisticPage_filterBtn ${timeRange === 'month' ? 'statisticPage_filterBtn--active' : ''}`}
                        onClick={() => setTimeRange('month')}
                    >
                        {translate('statistic_month')}
                    </button>
                    <button 
                        className={`statisticPage_filterBtn ${timeRange === 'week' ? 'statisticPage_filterBtn--active' : ''}`}
                        onClick={() => setTimeRange('week')}
                    >
                        {translate('statistic_week')}
                    </button>
                    <button 
                        className={`statisticPage_filterBtn ${timeRange === 'custom' ? 'statisticPage_filterBtn--active' : ''}`}
                        onClick={() => setTimeRange('custom')}
                    >
                        {translate('statistic_custom')}
                    </button>
                </div>
                {timeRange === 'custom' && (
                    <div className="statisticPage_customRange">
                        <input 
                            type="date" 
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="statisticPage_dateInput"
                        />
                        <span className="statisticPage_dateSeparator">—</span>
                        <input 
                            type="date" 
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="statisticPage_dateInput"
                        />
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="statisticPage_loading">
                    <Spinner />
                </div>
            ) : (
                <div className="statisticPage_content">
                    <div className="statisticPage_row">
                        <div className="statisticPage_widget statisticPage_widget--bubbles">
                            {wordsWidgetData ? (
                                <div className="statisticPage_wordStats_container">
                                    <div className="statisticPage_wordStats_header">
                                        <button 
                                            className="statisticPage_backBtn"
                                            onClick={handleWordBack}
                                            title={translate('common_back') || 'Back'}
                                        >
                                            <div className="statisticPage_backIcon"></div>
                                        </button>
                                        <div className="statisticPage_wordStats_title">
                                            {wordsWidgetData.totalCount} {translate('times')}
                                        </div>
                                    </div>
                                    <div className="statisticPage_wordStats_subtitle">
                                        {translate('word_appeared_in_sessions')
                                            ?.replace('[word]', wordsWidgetData.word)
                                            ?.replace('[count]', String(wordsWidgetData.counts.filter(c => c.value > 0).length))
                                            || `"${wordsWidgetData.word}" appeared in ${wordsWidgetData.counts.filter(c => c.value > 0).length} sessions`}
                                    </div>
                                    <Diagram points={wordsWidgetData.counts} activeIndex={wordsWidgetData.index} />
                                </div>
                            ) : (
                                <>
                                    <div className="statisticPage_widgetTitle">{translate('statistic_most_used_words')}</div>
                                    {widgetData.bubblesData.length > 0 ? (
                                        <Bubbles 
                                            points={widgetData.bubblesData}
                                            onSelect={(word) => setSelectedWord(word)}
                                            layout="packed"
                                        />
                                    ) : (
                                        <div className="statisticPage_noData">{translate('statistic_no_data')}</div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="statisticPage_widget statisticPage_widget--mostWord">
                            <MostCommonWord 
                                word={widgetData.mostCommonWord.word || '-'}
                                count={widgetData.mostCommonWord.count}
                            />
                        </div>
                    </div>
                    
                    <div className="statisticPage_row">
                        <div className="statisticPage_widget statisticPage_widget--speed">
                            {widgetData.speedData.speeds.length > 0 ? (
                                <SpeedChart
                                    values={widgetData.speedData.speeds}
                                    timeMarks={widgetData.speedData.sessionNumbers}
                                    average={widgetData.speedData.average}
                                    title={translate('statistic_average_speed')}
                                    subtitle={translate('statistic_speed_subtitle')}
                                    bottomTextA={translate('statistic_avg_speed_label')}
                                    bottomTextB={translate('meetingPage_words_per_minute')}
                                    sessionLabel={translate('statistic_session')}
                                />
                            ) : (
                                <div className="statisticPage_noData">{translate('statistic_no_data')}</div>
                            )}
                        </div>
                    </div>

                    <div className="statisticPage_row">
                        <div className="statisticPage_widget statisticPage_widget--volume">
                            {widgetData.volumeData.values.length > 0 ? (
                                <VolumeChart
                                    values={widgetData.volumeData.values}
                                    labels={widgetData.volumeData.labels}
                                    title={translate('statistic_volume')}
                                    subtitle={translate('statistic_volume_subtitle')}
                                    bottomTextA={translate('statistic_avg_volume')}
                                    bottomTextB="db"
                                    useSessionNumbers={true}
                                    sessionLabel={translate('statistic_session')}
                                />
                            ) : (
                                <div className="statisticPage_noData">{translate('statistic_no_data')}</div>
                            )}
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};
